import { io } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.roomCode = null;
    this.isHost = false;
    this.listeners = {};
  }

  connect() {
    if (this.socket?.connected) {
      return Promise.resolve(this.socket);
    }

    return new Promise((resolve, reject) => {
      try {
        // Use /api/socket.io path to work with Kubernetes ingress
        this.socket = io(BACKEND_URL, {
          path: '/api/socket.io/',
          transports: ['polling', 'websocket'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 20000,
          forceNew: true
        });

        this.socket.on('connect', () => {
          console.log('Socket connected:', this.socket.id);
          this.connected = true;
          resolve(this.socket);
        });

        this.socket.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          reject(error);
        });

        this.socket.on('disconnect', (reason) => {
          console.log('Socket disconnected:', reason);
          this.connected = false;
        });

        this.socket.on('error', (error) => {
          console.error('Socket error:', error);
          this._emit('error', error);
        });

      } catch (error) {
        console.error('Failed to create socket:', error);
        reject(error);
      }
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.roomCode = null;
      this.isHost = false;
    }
  }

  // Create a new game room
  createRoom(playerName) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const handleRoomCreated = (data) => {
        this.roomCode = data.room_code;
        this.isHost = true;
        this.socket.off('room_created', handleRoomCreated);
        this.socket.off('error', handleError);
        resolve(data);
      };

      const handleError = (error) => {
        this.socket.off('room_created', handleRoomCreated);
        this.socket.off('error', handleError);
        reject(new Error(error.message));
      };

      this.socket.on('room_created', handleRoomCreated);
      this.socket.on('error', handleError);

      this.socket.emit('create_room', { player_name: playerName });
    });
  }

  // Join an existing room
  joinRoom(roomCode, playerName) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const handleRoomJoined = (data) => {
        this.roomCode = data.room_code;
        this.isHost = false;
        this.socket.off('room_joined', handleRoomJoined);
        this.socket.off('error', handleError);
        resolve(data);
      };

      const handleError = (error) => {
        this.socket.off('room_joined', handleRoomJoined);
        this.socket.off('error', handleError);
        reject(new Error(error.message));
      };

      this.socket.on('room_joined', handleRoomJoined);
      this.socket.on('error', handleError);

      this.socket.emit('join_room', { 
        room_code: roomCode.toUpperCase(), 
        player_name: playerName 
      });
    });
  }

  // Start the game (host only)
  startGame() {
    if (!this.socket?.connected || !this.roomCode) {
      console.error('Cannot start game: not connected or no room');
      return;
    }
    this.socket.emit('start_game', { room_code: this.roomCode });
  }

  // Flip the book
  flipBook() {
    if (!this.socket?.connected || !this.roomCode) {
      console.error('Cannot flip: not connected or no room');
      return;
    }
    this.socket.emit('flip_book', { room_code: this.roomCode });
  }

  // Switch to next player
  nextPlayer() {
    if (!this.socket?.connected || !this.roomCode) {
      console.error('Cannot switch player: not connected or no room');
      return;
    }
    this.socket.emit('next_player', { room_code: this.roomCode });
  }

  // Leave the room
  leaveRoom() {
    if (this.socket?.connected && this.roomCode) {
      this.socket.emit('leave_room', { room_code: this.roomCode });
    }
    this.roomCode = null;
    this.isHost = false;
  }

  // Get room state
  getRoomState() {
    if (!this.socket?.connected || !this.roomCode) {
      return;
    }
    this.socket.emit('get_room_state', { room_code: this.roomCode });
  }

  // Event listeners
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
    
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  _emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  // Reattach listeners after reconnect
  reattachListeners() {
    if (this.socket) {
      Object.entries(this.listeners).forEach(([event, callbacks]) => {
        callbacks.forEach(callback => {
          this.socket.on(event, callback);
        });
      });
    }
  }

  // Getters
  get isConnected() {
    return this.socket?.connected || false;
  }

  get currentRoom() {
    return this.roomCode;
  }

  get amIHost() {
    return this.isHost;
  }
}

// Singleton instance
const socketService = new SocketService();
export default socketService;
